import React, { useState, useRef, useLayoutEffect, cloneElement } from 'react';

const DefaultHomeIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);
const DefaultCompassIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="m16.24 7.76-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12z" />
  </svg>
);
const DefaultBellIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

const defaultNavItems = [
  { id: 'default-home', icon: <DefaultHomeIcon />, label: 'Home' },
  { id: 'default-explore', icon: <DefaultCompassIcon />, label: 'Explore' },
  { id: 'default-notifications', icon: <DefaultBellIcon />, label: 'Notifications' },
];

/**
 * Adaptive-width navigation bar with a "limelight" effect that highlights the active item.
 *
 * Note on shadow color: the original source uses `shadow-[0_50px_15px_var(--primary)]`,
 * which assumes `--primary` is a usable CSS color. In shadcn HSL-split setups
 * (`--primary: 188 89% 36%`) that value alone is not a color. Pass an explicit
 * shadow via `limelightClassName` (e.g. `"bg-amber-400 shadow-[0_30px_18px_rgba(251,191,36,0.55)]"`).
 */
export const LimelightNav = ({
  items = defaultNavItems,
  activeIndex: controlledActiveIndex,
  defaultActiveIndex = 0,
  onTabChange,
  className = '',
  limelightClassName = '',
  iconContainerClassName = '',
  iconClassName = '',
}) => {
  const isControlled = controlledActiveIndex !== undefined;
  const [internalActiveIndex, setInternalActiveIndex] = useState(defaultActiveIndex);
  const activeIndex = isControlled ? controlledActiveIndex : internalActiveIndex;

  const [isReady, setIsReady] = useState(false);
  const navItemRefs = useRef([]);
  const limelightRef = useRef(null);

  useLayoutEffect(() => {
    if (items.length === 0) return;
    const limelight = limelightRef.current;
    const activeItem = navItemRefs.current[activeIndex];
    if (limelight && activeItem) {
      const newLeft = activeItem.offsetLeft + activeItem.offsetWidth / 2 - limelight.offsetWidth / 2;
      limelight.style.left = `${newLeft}px`;
      if (!isReady) {
        const t = setTimeout(() => setIsReady(true), 50);
        return () => clearTimeout(t);
      }
    }
  }, [activeIndex, isReady, items]);

  if (items.length === 0) return null;

  const handleItemClick = (index, itemOnClick) => {
    if (!isControlled) setInternalActiveIndex(index);
    onTabChange?.(index);
    itemOnClick?.();
  };

  return (
    <nav
      className={`relative inline-flex items-center h-16 rounded-lg bg-card text-foreground border px-2 ${className}`}
      aria-label="Navigation principale"
    >
      {items.map(({ id, icon, label, onClick }, index) => (
        <a
          key={id}
          ref={(el) => (navItemRefs.current[index] = el)}
          className={`relative z-20 flex h-full cursor-pointer items-center justify-center p-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md ${iconContainerClassName}`}
          onClick={(e) => {
            e.preventDefault();
            handleItemClick(index, onClick);
          }}
          href="#"
          aria-label={label}
          aria-current={activeIndex === index ? 'page' : undefined}
          title={label}
        >
          {cloneElement(icon, {
            'aria-hidden': true,
            className: `w-6 h-6 transition-opacity duration-200 ease-in-out ${
              activeIndex === index ? 'opacity-100' : 'opacity-40'
            } ${icon.props.className || ''} ${iconClassName || ''}`,
          })}
        </a>
      ))}

      <div
        ref={limelightRef}
        aria-hidden="true"
        className={`absolute top-0 z-10 w-11 h-[5px] rounded-full bg-primary ${
          isReady ? 'transition-[left] duration-[400ms] ease-in-out' : ''
        } ${limelightClassName}`}
        style={{ left: '-999px' }}
      >
        <div className="absolute left-[-30%] top-[5px] w-[160%] h-14 [clip-path:polygon(5%_100%,25%_0,75%_0,95%_100%)] bg-gradient-to-b from-current/30 to-transparent pointer-events-none opacity-50" />
      </div>
    </nav>
  );
};
